import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

// ── Firestore fetch ───────────────────────────────────────────────────────
const PROJECT_ID = 'portfolio-e79b8-5854c';
const API_KEY    = 'AIzaSyDLsmnqt6wth0b3hZYrnllXnJIcjUaK8Uc';
const REST_BASE  = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`;

function fromValue(v) {
  if (!v) return null;
  if ('stringValue'  in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue'  in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue'    in v) return null;
  if ('arrayValue'   in v) return (v.arrayValue.values || []).map(fromValue);
  if ('mapValue'     in v) return fromFields(v.mapValue.fields || {});
  return null;
}
function fromFields(fields) {
  const obj = {};
  for (const [k, v] of Object.entries(fields)) obj[k] = fromValue(v);
  return obj;
}

async function fetchImageConfig() {
  const url = `${REST_BASE}/imageConfig/tools?key=${API_KEY}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) return null;
  const json = await res.json();
  return fromFields(json.fields || {});
}

// ── Default extra-image object ────────────────────────────────────────────
export const EXTRA_IMAGE_DEFAULTS = {
  url:               '',
  hidden:            false,
  label:             'Extra image',
  startX_desktop:   -30,
  startY_desktop:    90,
  startX_mobile:    -60,
  startY_mobile:     80,
  endX_desktop:      95,
  endY_desktop:     -95,
  endX_mobile:      100,
  endY_mobile:      -90,
  trail:             0.45,
  img2FadeStart:     0.1,
  rot2InitOffset:    -18,
  pA_desktop:        0.22,
  pB_desktop:        0.62,
  rot2AtA_desktop:   -40,
  rot2AtB_desktop:   -30,
  pA_mobile:         0.48,
  pB_mobile:         0.52,
  rot2AtA_mobile:    -60,
  rot2AtB_mobile:    -60,
  widthVw_desktop:   20,
  widthVw_mobile:    44,
  zIndex:            8,
};

// ── Default config ────────────────────────────────────────────────────────
const DEFAULTS = {
  textOverline:   'Philosophy',
  textLine1:      'Craft over',
  textLine2:      'shortcuts.',
  textLine3:      'Always.',
  textLine1Color: 'var(--white)',
  textLine2Color: 'var(--acid,)',
  textLine3Color: 'var(--white)',
  textSub: 'I believe great digital products are built through attention to detail, thoughtful design, and clean engineering. Every project is an opportunity to create experiences that are fast, intuitive, and meaningful. Rather than chasing trends, I focus on building solutions that are reliable, scalable, and crafted to deliver real value.',

  img1StartX_desktop:   -30,
  img1StartY_desktop:    90,
  img1StartRot:         -12,
  img1EndX_desktop:      95,
  img1EndY_desktop:     -95,
  img1EndRot:            10,
  img1pA_desktop:        0.0,
  img1pB_desktop:        0.0,
  img1RotAtA_desktop:   -12,
  img1RotAtB_desktop:    10,

  img1StartX_mobile:    -60,
  img1StartY_mobile:     80,
  img1EndX_mobile:      100,
  img1EndY_mobile:      -90,
  img1pA_mobile:         0.0,
  img1pB_mobile:         0.0,
  img1RotAtA_mobile:    -12,
  img1RotAtB_mobile:     10,

  trail:                0.38,
  rot2InitOffset:      -18,
  pA_desktop:           0.22,
  pB_desktop:           0.62,
  rot2AtA_desktop:     -40,
  rot2AtB_desktop:     -30,
  pA_mobile:            0.48,
  pB_mobile:            0.52,
  rot2AtA_mobile:      -60,
  rot2AtB_mobile:      -60,

  pinLength:           220,

  img1Width_desktop:    24,
  img1Width_mobile:     52,
  img2Width_desktop:    20,
  img2Width_mobile:     44,

  img1Url: './rock-bg.png',
  img2Url: './miss.png',

  // ── Visibility flags for core images ────────────────────────────────
  img1Hidden: false,
  img2Hidden: false,

  extraImages: [],
  img2FadeStart: 0.1,
};

// ── Smooth cubic ease ─────────────────────────────────────────────────────
function smoothstep(t) { return t * t * (3 - 2 * t); }

// ── Compute rotation along the 3-segment curve ───────────────────────────
function computeRot(p, rotStart, pA, pB, rotAtA, rotAtB) {
  if (p <= pA) {
    const t = pA === 0 ? 1 : p / pA;
    return rotStart + (rotAtA - rotStart) * smoothstep(t);
  }
  if (p <= pB) {
    const t = (pB === pA) ? 1 : (p - pA) / (pB - pA);
    return rotAtA + (rotAtB - rotAtA) * smoothstep(t);
  }
  return rotAtB;
}

// ── Merge an extra-image object with defaults ─────────────────────────────
function mergeExtra(raw) {
  if (typeof raw === 'string') {
    return { ...EXTRA_IMAGE_DEFAULTS, url: raw };
  }
  return { ...EXTRA_IMAGE_DEFAULTS, ...raw };
}

export default function Tools({ loaded, isMobile }) {
  const sectionRef  = useRef(null);
  const imgRef      = useRef(null);
  const img2Ref     = useRef(null);
  const line1Ref    = useRef(null);
  const line2Ref    = useRef(null);
  const line3Ref    = useRef(null);
  const subRef      = useRef(null);
  const overlineRef = useRef(null);

  const extraRefsRef = useRef([]);

  const [imgCfg, setImgCfg] = useState(null);

  // ── Load config ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchImageConfig()
      .then(remote => {
        if (remote) {
          const extras = Array.isArray(remote.extraImages)
            ? remote.extraImages.map(mergeExtra)
            : [];
          setImgCfg({ ...DEFAULTS, ...remote, extraImages: extras });
        } else {
          setImgCfg({ ...DEFAULTS });
        }
      })
      .catch(() => setImgCfg({ ...DEFAULTS }));
  }, []);

  // ── GSAP ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!loaded || !imgCfg) return;

    function vwpx(val) { return (val / 100) * window.innerWidth; }
    function vhpx(val) { return (val / 100) * window.innerHeight; }

    const startX   = vwpx(isMobile ? imgCfg.img1StartX_mobile : imgCfg.img1StartX_desktop);
    const startY   = vhpx(isMobile ? imgCfg.img1StartY_mobile : imgCfg.img1StartY_desktop);
    const startRot = imgCfg.img1StartRot;
    const endX     = vwpx(isMobile ? imgCfg.img1EndX_mobile   : imgCfg.img1EndX_desktop);
    const endY     = vhpx(isMobile ? imgCfg.img1EndY_mobile   : imgCfg.img1EndY_desktop);
    const endRot   = imgCfg.img1EndRot;

    const img1Rot = isMobile
      ? { pA: imgCfg.img1pA_mobile,  pB: imgCfg.img1pB_mobile,  rotAtA: imgCfg.img1RotAtA_mobile,  rotAtB: imgCfg.img1RotAtB_mobile  }
      : { pA: imgCfg.img1pA_desktop, pB: imgCfg.img1pB_desktop, rotAtA: imgCfg.img1RotAtA_desktop, rotAtB: imgCfg.img1RotAtB_desktop };

    const TRAIL      = imgCfg.trail;
    const FADE_START = imgCfg.img2FadeStart;
    const rot2Start  = startRot + imgCfg.rot2InitOffset;
    const rot2Config = isMobile
      ? { pA: imgCfg.pA_mobile, pB: imgCfg.pB_mobile, rot2AtA: imgCfg.rot2AtA_mobile, rot2AtB: imgCfg.rot2AtB_mobile }
      : { pA: imgCfg.pA_desktop, pB: imgCfg.pB_desktop, rot2AtA: imgCfg.rot2AtA_desktop, rot2AtB: imgCfg.rot2AtB_desktop };

    const PIN_END = `+=${imgCfg.pinLength}%`;

    // Whether each core image is hidden
    const img1Hidden = imgCfg.img1Hidden ?? false;
    const img2Hidden = imgCfg.img2Hidden ?? false;

    const visibleExtras = (imgCfg.extraImages || [])
      .map(mergeExtra)
      .filter(ex => !ex.hidden && ex.url);

    const ctx = gsap.context(() => {
      // ── Initial positions ────────────────────────────────────────────
      // Set initial positions even for hidden images so positional logic stays consistent
      gsap.set(imgRef.current,  { x: startX, y: startY, rotation: startRot });
      gsap.set(img2Ref.current, { x: startX, y: startY, rotation: rot2Start, opacity: 0 });

      visibleExtras.forEach((ex, i) => {
        const el = extraRefsRef.current[i];
        if (!el) return;
        const exStartX   = vwpx(isMobile ? ex.startX_mobile : ex.startX_desktop);
        const exStartY   = vhpx(isMobile ? ex.startY_mobile : ex.startY_desktop);
        const exRotStart = startRot + ex.rot2InitOffset;
        gsap.set(el, { x: exStartX, y: exStartY, rotation: exRotStart, opacity: 0 });
      });

      // ── Text entrance ────────────────────────────────────────────────
      const lines = [line1Ref, line2Ref, line3Ref].map(r => r.current).filter(Boolean);
      gsap.set(overlineRef.current, { opacity: 0, y: 20 });
      gsap.set(subRef.current,      { opacity: 0, y: 24 });

      const splits = lines
        .map(el => el ? new SplitText(el, { type: 'chars,words' }) : null)
        .filter(Boolean);

      splits.forEach(split => {
        gsap.set(split.chars, { yPercent: 115, opacity: 0, skewX: -8, rotateX: 40 });
      });

      const tl = gsap.timeline({ delay: 0.15 });
      tl.to(overlineRef.current, { opacity: 1, y: 0, duration: 0.8, ease: 'expo.out' });

      splits.forEach((split, i) => {
        tl.to(split.chars, {
          yPercent: 0, opacity: 1, skewX: 0, rotateX: 0,
          duration: 1.05, ease: 'expo.out',
          stagger: { each: 0.022, from: 'start' },
        }, i === 0 ? '-=0.45' : '-=0.72');
      });

      const allChars = splits.flatMap(s => s.chars);

      tl.to(allChars, {
        color: 'var(--acid)',
        duration: 0.07,
        stagger: { each: 0.005, from: 'random' },
        ease: 'none',
      }, '-=0.35');

      tl.to(allChars, {
        color: (i, el) => el.closest('[data-color]')?.dataset.color ?? 'var(--white)',
        duration: 0.18,
        stagger: { each: 0.006, from: 'random' },
        ease: 'power1.out',
      }, '-=0.22');

      tl.to(subRef.current, { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out' }, '-=0.4');

      gsap.to(allChars, {
        y: isMobile ? -4 : -9,
        duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1,
        stagger: { each: 0.045, from: 'random' },
        delay: 2.2,
      });

      // ── Scroll pin ───────────────────────────────────────────────────
      const pinTrigger = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: PIN_END,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
      });

      // ── Scroll-driven motion ─────────────────────────────────────────
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: PIN_END,
        scrub: 0.5,
        onUpdate(self) {
          const p1 = self.progress;

          // ── Image 1 ──────────────────────────────────────────────────
          // Always compute position (used as reference for img2/extras),
          // but skip the gsap.set if hidden (DOM element has display:none)
          const rot1 = (img1Rot.pA === 0 && img1Rot.pB === 0)
            ? startRot + (endRot - startRot) * p1
            : computeRot(p1, startRot, img1Rot.pA, img1Rot.pB, img1Rot.rotAtA, img1Rot.rotAtB);

          if (!img1Hidden && imgRef.current) {
            gsap.set(imgRef.current, {
              x: startX + (endX - startX) * p1,
              y: startY + (endY - startY) * p1,
              rotation: rot1,
            });
          }

          // ── Image 2 ──────────────────────────────────────────────────
          if (!img2Hidden && img2Ref.current) {
            const p2  = Math.max(0, p1 - TRAIL) / (1 - TRAIL);
            const op2 = Math.min(1, p2 / Math.max(FADE_START, 0.001));
            const rot2 = computeRot(p2, rot2Start, rot2Config.pA, rot2Config.pB, rot2Config.rot2AtA, rot2Config.rot2AtB);

            gsap.set(img2Ref.current, {
              x: startX + (endX - startX) * p2,
              y: startY + (endY - startY) * p2,
              rotation: rot2,
              opacity: op2,
            });
          }

          // ── Extra images ─────────────────────────────────────────────
          visibleExtras.forEach((ex, i) => {
            const el = extraRefsRef.current[i];
            if (!el) return;

            const exStartX = vwpx(isMobile ? ex.startX_mobile : ex.startX_desktop);
            const exStartY = vhpx(isMobile ? ex.startY_mobile : ex.startY_desktop);
            const exEndX   = vwpx(isMobile ? ex.endX_mobile   : ex.endX_desktop);
            const exEndY   = vhpx(isMobile ? ex.endY_mobile   : ex.endY_desktop);

            const exTrail    = ex.trail ?? 0.45;
            const exFade     = ex.img2FadeStart ?? 0.1;
            const exRotStart = startRot + (ex.rot2InitOffset ?? -18);

            const exCurve = isMobile
              ? { pA: ex.pA_mobile ?? 0.48, pB: ex.pB_mobile ?? 0.52, rotAtA: ex.rot2AtA_mobile ?? -60, rotAtB: ex.rot2AtB_mobile ?? -60 }
              : { pA: ex.pA_desktop ?? 0.22, pB: ex.pB_desktop ?? 0.62, rotAtA: ex.rot2AtA_desktop ?? -40, rotAtB: ex.rot2AtB_desktop ?? -30 };

            const pEx  = Math.max(0, p1 - exTrail) / (1 - exTrail);
            const opEx = Math.min(1, pEx / Math.max(exFade, 0.001));
            const rotEx = computeRot(pEx, exRotStart, exCurve.pA, exCurve.pB, exCurve.rotAtA, exCurve.rotAtB);

            gsap.set(el, {
              x: exStartX + (exEndX - exStartX) * pEx,
              y: exStartY + (exEndY - exStartY) * pEx,
              rotation: rotEx,
              opacity: opEx,
            });
          });
        },
      });

      return () => pinTrigger.kill();
    }, sectionRef);

    return () => ctx.revert();
  }, [loaded, isMobile, imgCfg]);

  if (!imgCfg) return null;

  const img1Url    = imgCfg.img1Url    ?? DEFAULTS.img1Url;
  const img2Url    = imgCfg.img2Url    ?? DEFAULTS.img2Url;
  const img1Hidden = imgCfg.img1Hidden ?? false;
  const img2Hidden = imgCfg.img2Hidden ?? false;

  const visibleExtras = (imgCfg.extraImages || [])
    .map(mergeExtra)
    .filter(ex => !ex.hidden && ex.url);

  extraRefsRef.current = extraRefsRef.current.slice(0, visibleExtras.length);

  return (
    <section
      ref={sectionRef}
      style={{
        position: 'relative',
        height: '100vh',
        background: 'var(--black)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* ── Text ── */}
      <div style={{
        position: 'relative',
        zIndex: 5,
        textAlign: 'center',
        padding: 0,
        maxWidth: '100%',
        width: '100%',
      }}>
        <div
          ref={overlineRef}
          style={{
            fontFamily: 'Space Mono, monospace',
            fontSize: isMobile ? '0.72rem' : '0.9rem',
            color: 'var(--acid)',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            marginBottom: isMobile ? '1.4rem' : '2rem',
          }}
                                     data-cursor-text="Scroll
 down"     


        >
          {imgCfg.textOverline ?? DEFAULTS.textOverline}
        </div>

        <div
          ref={line1Ref}
          data-color={imgCfg.textLine1Color ?? DEFAULTS.textLine1Color}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? 'clamp(2.2rem, 11vw, 3.6rem)' : 'clamp(3.5rem, 7vw, 7rem)',
            lineHeight: 1.0,
            letterSpacing: '-0.04em',
            color: imgCfg.textLine1Color ?? DEFAULTS.textLine1Color,
            marginBottom: '0.1em',
            overflow: 'hidden',
            perspective: '600px',
          }}
                                     data-cursor-text="Scroll
 down"     


        >
          {imgCfg.textLine1 ?? DEFAULTS.textLine1}
        </div>

        <div
          ref={line2Ref}
          data-color={imgCfg.textLine2Color ?? DEFAULTS.textLine2Color}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? 'clamp(2.2rem, 11vw, 3.6rem)' : 'clamp(3.5rem, 7vw, 7rem)',
            lineHeight: 1.0,
            letterSpacing: '-0.04em',
            color: imgCfg.textLine2Color ?? DEFAULTS.textLine2Color,
            marginBottom: '0.1em',
            overflow: 'hidden',
            perspective: '600px',
          }}
                                     data-cursor-text="Scroll
 down"     


        >
          {imgCfg.textLine2 ?? DEFAULTS.textLine2}
        </div>

        <div
          ref={line3Ref}
          data-color={imgCfg.textLine3Color ?? DEFAULTS.textLine3Color}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? 'clamp(2.2rem, 11vw, 3.6rem)' : 'clamp(3.5rem, 7vw, 7rem)',
            lineHeight: 1.0,
            letterSpacing: '-0.04em',
            color: imgCfg.textLine3Color ?? DEFAULTS.textLine3Color,
            marginBottom: isMobile ? '2rem' : '3rem',
            overflow: 'hidden',
            perspective: '600px',
          }}
                                     data-cursor-text="Scroll
 down"     


        >
          {imgCfg.textLine3 ?? DEFAULTS.textLine3}
        </div>

        <p
          ref={subRef}
          style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: isMobile ? '1rem' : 'clamp(1.05rem, 1.5vw, 1.3rem)',
            color: 'var(--muted)',
            lineHeight: 1.85,
            margin: '0 auto',
            maxWidth: isMobile ? '90%' : '52ch',
          }}
        >
          {imgCfg.textSub ?? DEFAULTS.textSub}
        </p>
      </div>

      {/* ── Extra images (behind img2) ── */}
      {visibleExtras.map((ex, i) => (
        <img
          key={i}
          ref={el => { extraRefsRef.current[i] = el; }}
          src={ex.url}
          alt={ex.label || `Extra image ${i + 1}`}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: isMobile
              ? `${ex.widthVw_mobile ?? EXTRA_IMAGE_DEFAULTS.widthVw_mobile}vw`
              : `${ex.widthVw_desktop ?? EXTRA_IMAGE_DEFAULTS.widthVw_desktop}vw`,
            height: 'auto',
            borderRadius: '12px',
            willChange: 'transform, opacity',
            display: 'block',
            zIndex: ex.zIndex ?? 8,
          }}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      ))}

      {/* ── Image 2 — trails img1 ── */}
      {!img2Hidden && (
        <img
          ref={img2Ref}
          src={img2Url}
          alt="Profile 2"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: isMobile
              ? `${imgCfg.img2Width_mobile ?? DEFAULTS.img2Width_mobile}vw`
              : `${imgCfg.img2Width_desktop ?? DEFAULTS.img2Width_desktop}vw`,
            height: 'auto',
            borderRadius: '12px',
            willChange: 'transform, opacity',
            display: 'block',
            zIndex: 9,
          }}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      {/* Keep ref alive even when hidden so GSAP context doesn't break on re-render */}
      {img2Hidden && <span ref={img2Ref} style={{ display: 'none' }} aria-hidden="true" />}

      {/* ── Image 1 — leads ── */}
      {!img1Hidden && (
        <img
          ref={imgRef}
          src={img1Url}
          alt="Profile"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: isMobile
              ? `${imgCfg.img1Width_mobile ?? DEFAULTS.img1Width_mobile}vw`
              : `${imgCfg.img1Width_desktop ?? DEFAULTS.img1Width_desktop}vw`,
            height: 'auto',
            borderRadius: '12px',
            willChange: 'transform',
            display: 'block',
            zIndex: 10,
          }}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      {/* Keep ref alive even when hidden */}
      {img1Hidden && <span ref={imgRef} style={{ display: 'none' }} aria-hidden="true" />}
    </section>
  );
}